import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { Topheader } from '../../components/Topheader';
import { renderAnnouncementMessage } from '../../components/Announcement/Announcement';
import { DataGetApi, DataPostApiV2 } from '../../helpers/PostDataApi';
import config from '../../constants/config';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PropTypes from 'prop-types';
import '../../components/Announcement/Announcement.css';
import './AnnouncementSettings.css';

const initialAnnouncement = { enabled: false, title: '', message: '', modalMessage: '' };

const AnnouncementSettings = (props) => {
  const groupId = props.match?.params?.groupId;
  const [announcement, setAnnouncement] = useState(initialAnnouncement);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('user') || '{}')?.token;
    const endpoint = `${config.BASEURL}announcement/groups/${groupId}`;
    DataGetApi(endpoint, {}, token).then((response) => {
      const loadedAnnouncement = response?.data?.announcement?.announcement;
      if (loadedAnnouncement || groupId) {
        setAnnouncement(loadedAnnouncement || initialAnnouncement);
        setIsLoaded(true);
      } else {
        setSaveMessage("Couldn't load the current announcement. Reload before saving.");
      }
    });
  }, [groupId]);

  const updateField = (field, value) => setAnnouncement((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setIsConfirmationOpen(false);
    setIsSaving(true);
    setSaveMessage('');
    try {
      const response = await DataPostApiV2(`${config.BASEURL}announcement/groups/${groupId}`, announcement);
      if (response.status === 200 || response.status === 201) {
        const savedAnnouncement = response.data.announcement;
        setAnnouncement(savedAnnouncement);
        setSaveMessage('Announcement saved.');
      } else {
        setSaveMessage('Unable to save the announcement.');
      }
    } catch {
      setSaveMessage('Unable to save the announcement.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rightPanel">
      <Topheader />
      <main className="contentPart announcementSettings">
        {groupId && (
          <Button component={Link} startIcon={<ArrowBackIcon />} to={`/Announcement/groups/${groupId}`}>
            Back to group
          </Button>
        )}
        <Typography component="h1" variant="h5">
          Edit group announcement
        </Typography>
        <Paper className="announcementSettingsPanel">
          <div className="announcementSettingsLayout">
            <div className="announcementSettingsFields">
              <FormControlLabel
                control={
                  <Switch
                    checked={announcement.enabled}
                    onChange={(event) => updateField('enabled', event.target.checked)}
                  />
                }
                label="Show announcement banner"
              />
              <TextField
                fullWidth
                label="Title"
                margin="normal"
                value={announcement.title}
                onChange={(event) => updateField('title', event.target.value)}
              />
              <TextField
                fullWidth
                label="Banner text"
                margin="normal"
                multiline
                minRows={5}
                name="message"
                value={announcement.message}
                onChange={(event) => updateField('message', event.target.value)}
              />
              <TextField
                fullWidth
                label="Popup text"
                margin="normal"
                multiline
                minRows={5}
                name="modalMessage"
                value={announcement.modalMessage || ''}
                onChange={(event) => updateField('modalMessage', event.target.value)}
              />
              <Typography className="announcementSettingsHelp" color="textSecondary" variant="caption">
                Links use [text](https://url). Images use ![alt text](https://image-url). Refresh the page for changes
                to take effect.
              </Typography>
              <div className="announcementSettingsActions">
                {saveMessage && <Typography role="status">{saveMessage}</Typography>}
                <Button
                  color="primary"
                  disabled={isSaving || !isLoaded}
                  onClick={() => setIsConfirmationOpen(true)}
                  variant="contained"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
            <div className="announcementSettingsPreviews">
              <div className="announcementBannerPreview" aria-label="Banner preview">
                <Typography className="announcementPreviewLabel" color="textSecondary" variant="overline">
                  Banner preview
                </Typography>
                <section className="announcementBanner" aria-label="Announcement banner preview">
                  <div>
                    <strong>{announcement.title || 'Announcement title'}</strong>
                    <span>{renderAnnouncementMessage(announcement.message || 'Banner text')}</span>
                  </div>
                  <Button
                    className="announcementBannerButton"
                    color="inherit"
                    onClick={(event) => event.preventDefault()}
                    size="small"
                    tabIndex={-1}
                    variant="outlined"
                  >
                    View details
                  </Button>
                </section>
              </div>
              <div className="announcementModalPreview" role="dialog" aria-label="Modal preview">
                <Typography className="announcementModalPreviewLabel" color="textSecondary" variant="overline">
                  Popup preview
                </Typography>
                <div className="announcementModalPreviewWindow">
                  <Typography className="announcementModalPreviewTitle" component="h2" variant="h6">
                    {announcement.title || 'Announcement title'}
                  </Typography>
                  <div className="announcementModalPreviewMessage">
                    {renderAnnouncementMessage(announcement.modalMessage || announcement.message)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Paper>
        <Dialog
          open={isConfirmationOpen}
          onClose={() => setIsConfirmationOpen(false)}
          aria-labelledby="announcement-save-confirmation-title"
          aria-describedby="announcement-save-confirmation-description"
        >
          <DialogTitle id="announcement-save-confirmation-title">Publish announcement?</DialogTitle>
          <DialogContent>
            <DialogContentText id="announcement-save-confirmation-description">
              {groupId
                ? 'These changes will create a new version for every location in this group.'
                : 'These changes will be published to all Flex users.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsConfirmationOpen(false)} color="default">
              Cancel
            </Button>
            <Button onClick={save} color="primary" variant="contained" autoFocus>
              Publish
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
};

AnnouncementSettings.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      groupId: PropTypes.string,
    }),
  }),
};

export default AnnouncementSettings;
